import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Coins, ChevronDown, Swords, Target, AlertTriangle, MessageSquare } from 'lucide-react';
import { getBossImageUrl } from '../utils/bossImages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const urgentBlinkStyle = `
@keyframes urgentBlink {
  0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(239,68,68,0.8), inset 0 0 4px rgba(239,68,68,0.3); }
  50% { opacity: 0.6; box-shadow: 0 0 20px rgba(239,68,68,1), inset 0 0 10px rgba(239,68,68,0.5); }
}
@keyframes chatBlink {
  0%, 100% { box-shadow: 0 0 6px rgba(234,179,8,0.6); border-color: rgba(234,179,8,0.8); }
  50% { box-shadow: 0 0 16px rgba(234,179,8,1); border-color: rgba(234,179,8,1); }
}
@keyframes huntingBlink {
  0%, 100% { box-shadow: 0 0 6px rgba(59,130,246,0.6); border-color: rgba(59,130,246,0.8); }
  50% { box-shadow: 0 0 16px rgba(59,130,246,1); border-color: rgba(59,130,246,1); }
}
`;

export const ExpeditionQuickBar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myExps, setMyExps] = useState([]);
  const [myBounties, setMyBounties] = useState([]);
  const [unreadRooms, setUnreadRooms] = useState([]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem("token");
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [expRes, bountyRes, notifRes] = await Promise.all([
        axios.get(`${API_URL}/api/expeditions/my`, auth).catch(() => ({ data: { expeditions: [] } })),
        axios.get(`${API_URL}/api/bounties/my`, auth).catch(() => ({ data: { bounties: [] } })),
        axios.get(`${API_URL}/api/notifications`, auth).catch(() => ({ data: { rooms_with_unread_chat: [] } })),
      ]);
      setMyExps(expRes.data.expeditions || []);
      setMyBounties(bountyRes.data.bounties || []);
      setUnreadRooms(notifRes.data.rooms_with_unread_chat || []);
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  // Expeditions
  const preparing = myExps.filter(e => e.status === "ACTIVE");
  const ongoing = myExps.filter(e => e.status === "ONGOING");
  const needsConfirm = myExps.filter(e => e.status === "AWAITING_BUYER_CONFIRM" && e.buyer_id === user?.id);
  const settling = myExps.filter(e => 
    (e.status === "SETTLING_SPLITS") || 
    (e.status === "AWAITING_BUYER_CONFIRM" && e.buyer_id !== user?.id)
  );

  // Bounties
  const isBuyer = (b) => b.client_id === user?.id;
  const activeBounties = myBounties.filter(b => b.status === "ACTIVE" && isBuyer(b));
  
  // Red CONFIRM: other side confirmed, YOU still need to confirm
  const needsBountyConfirm = myBounties.filter(b => 
    b.status === "ASSIGNED" && (
      (b.client_id === user?.id && !b.client_confirmed && b.hunter_confirmed) ||
      (b.hunter_id === user?.id && !b.hunter_confirmed && b.client_confirmed)
    )
  );

  // Blue "Hunting"/"Accepted": assigned, neither confirmed yet or user already confirmed
  const assignedBlue = myBounties.filter(b => 
    b.status === "ASSIGNED" && !needsBountyConfirm.some(nc => nc.id === b.id)
  );

  // Chat helpers
  const expHasChat = (expId) => unreadRooms.includes(`expedition_${expId}`);
  const bountyHasChat = (bountyId) => unreadRooms.includes(`bounty_${bountyId}`);

  const hasExps = preparing.length > 0 || ongoing.length > 0 || settling.length > 0 || needsConfirm.length > 0;
  const hasBounties = activeBounties.length > 0 || needsBountyConfirm.length > 0 || assignedBlue.length > 0;

  if (!user || (!hasExps && !hasBounties)) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg bg-black/30 border border-purple-500/20 mb-3" data-testid="quick-expedition-bar">
      <style>{urgentBlinkStyle}</style>
      {/* === EXPEDITIONS === */}
      {hasExps && (
        <>
          <Swords className="w-4 h-4 text-purple-400/60" />
          {/* Needs buyer confirmation - URGENT */}
          {needsConfirm.map(exp => (
            <Button
              key={exp.id}
              size="sm"
              className="h-7 px-2 bg-red-700/40 hover:bg-red-600/60 text-red-100 border-2 border-red-500 text-[11px] gap-1"
              style={{ animation: "urgentBlink 0.8s ease-in-out infinite" }}
              onClick={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
              data-testid={`qbar-exp-confirm-${exp.id}`}
            >
              <AlertTriangle className="w-3.5 h-3.5 text-red-300" />
              {getBossImageUrl(exp.boss_image || exp.boss_name) && <img src={getBossImageUrl(exp.boss_image || exp.boss_name)} alt="" className="w-5 h-5 rounded" />}
              <span className="max-w-[80px] truncate font-bold">{exp.boss_name}</span>
              <Badge className="bg-red-500/50 text-white text-[8px] px-1 py-0 font-bold">CONFIRM!</Badge>
            </Button>
          ))}
          {preparing.map(exp => (
            <Button
              key={exp.id}
              size="sm"
              className={`h-7 px-2 text-[11px] gap-1 ${expHasChat(exp.id) ? 'bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-200 border-2 border-yellow-500' : 'bg-blue-600/20 hover:bg-blue-600/40 text-blue-200 border border-blue-500/30'}`}
              style={expHasChat(exp.id) ? { animation: "chatBlink 1.2s ease-in-out infinite" } : {}}
              onClick={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
              data-testid={`qbar-exp-prep-${exp.id}`}
            >
              {expHasChat(exp.id) && <MessageSquare className="w-3 h-3 text-yellow-300" />}
              {getBossImageUrl(exp.boss_image || exp.boss_name) && <img src={getBossImageUrl(exp.boss_image || exp.boss_name)} alt="" className="w-5 h-5 rounded" />}
              <span className="max-w-[80px] truncate">{exp.boss_name}</span>
              <Badge className="bg-blue-500/30 text-blue-200 text-[8px] px-1 py-0">Prep</Badge>
            </Button>
          ))}
          {ongoing.map(exp => (
            <Button
              key={exp.id}
              size="sm"
              className={`h-7 px-2 text-[11px] gap-1 ${expHasChat(exp.id) ? 'bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-200 border-2 border-yellow-500' : 'bg-green-600/20 hover:bg-green-600/40 text-green-200 border border-green-500/30'}`}
              style={expHasChat(exp.id) ? { animation: "chatBlink 1.2s ease-in-out infinite" } : {}}
              onClick={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
              data-testid={`qbar-exp-live-${exp.id}`}
            >
              {expHasChat(exp.id) && <MessageSquare className="w-3 h-3 text-yellow-300" />}
              {getBossImageUrl(exp.boss_image || exp.boss_name) && <img src={getBossImageUrl(exp.boss_image || exp.boss_name)} alt="" className="w-5 h-5 rounded" />}
              <span className="max-w-[80px] truncate">{exp.boss_name}</span>
              <Badge className="bg-green-500/30 text-green-200 text-[8px] px-1 py-0 animate-pulse">Live</Badge>
            </Button>
          ))}
          {settling.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-7 px-2 bg-amber-600/20 hover:bg-amber-600/40 text-amber-200 border border-amber-500/30 text-[11px] gap-1">
                  <Coins className="w-3.5 h-3.5" />
                  Settling ({settling.length})
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {settling.map(exp => (
                  <DropdownMenuItem key={exp.id} onClick={() => navigate(`/hunters-chance/expedition/${exp.id}`)} className="cursor-pointer gap-2">
                    {getBossImageUrl(exp.boss_image || exp.boss_name) && <img src={getBossImageUrl(exp.boss_image || exp.boss_name)} alt="" className="w-5 h-5 rounded" />}
                    <span>{exp.boss_name}</span>
                    <Badge className="text-[8px]">{exp.status === "AWAITING_BUYER_CONFIRM" ? "Awaiting" : "Splits"}</Badge>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
      )}

      {/* Divider */}
      {hasExps && hasBounties && <div className="w-px h-5 bg-white/10 mx-1" />}

      {/* === BOUNTIES === */}
      {hasBounties && (
        <>
          <Target className="w-4 h-4 text-rose-400/60" />
          {/* Needs confirmation - URGENT */}
          {needsBountyConfirm.map(b => (
            <Button
              key={b.id}
              size="sm"
              className="h-7 px-2 bg-red-700/40 hover:bg-red-600/60 text-red-100 border-2 border-red-500 text-[11px] gap-1"
              style={{ animation: "urgentBlink 0.8s ease-in-out infinite" }}
              onClick={() => navigate(`/hunters-chance/bounty/${b.id}`)}
              data-testid={`qbar-bounty-confirm-${b.id}`}
            >
              <AlertTriangle className="w-3.5 h-3.5 text-red-300" />
              {getBossImageUrl(b.boss_image || b.boss_name) && <img src={getBossImageUrl(b.boss_image || b.boss_name)} alt="" className="w-5 h-5 rounded" />}
              <span className="max-w-[80px] truncate font-bold">{b.boss_name}</span>
              <Badge className="bg-red-500/50 text-white text-[8px] px-1 py-0 font-bold">CONFIRM!</Badge>
            </Button>
          ))}
          {activeBounties.map(b => (
            <Button
              key={b.id}
              size="sm"
              className={`h-7 px-2 text-[11px] gap-1 ${bountyHasChat(b.id) ? 'bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-200 border-2 border-yellow-500' : 'bg-rose-600/20 hover:bg-rose-600/40 text-rose-200 border border-rose-500/30'}`}
              style={bountyHasChat(b.id) ? { animation: "chatBlink 1.2s ease-in-out infinite" } : {}}
              onClick={() => navigate(`/hunters-chance/bounty/${b.id}`)}
              data-testid={`qbar-bounty-active-${b.id}`}
            >
              {bountyHasChat(b.id) && <MessageSquare className="w-3 h-3 text-yellow-300" />}
              {getBossImageUrl(b.boss_image || b.boss_name) && <img src={getBossImageUrl(b.boss_image || b.boss_name)} alt="" className="w-5 h-5 rounded" />}
              <span className="max-w-[80px] truncate">{b.boss_name}</span>
              <Badge className="bg-rose-500/30 text-rose-200 text-[8px] px-1 py-0">Posted</Badge>
            </Button>
          ))}
          {assignedBlue.map(b => {
            const hasChat = bountyHasChat(b.id);
            const isHunting = b.hunter_id === user?.id;
            return (
              <Button
                key={b.id}
                size="sm"
                className={`h-7 px-2 text-[11px] gap-1 ${hasChat ? 'bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-200 border-2 border-yellow-500' : 'bg-blue-600/20 hover:bg-blue-600/40 text-blue-200 border-2 border-blue-500'}`}
                style={hasChat ? { animation: "chatBlink 1.2s ease-in-out infinite" } : { animation: "huntingBlink 1.5s ease-in-out infinite" }}
                onClick={() => navigate(`/hunters-chance/bounty/${b.id}`)}
                data-testid={`qbar-bounty-assigned-${b.id}`}
              >
                {hasChat ? <MessageSquare className="w-3 h-3 text-yellow-300" /> : <Swords className="w-3 h-3 text-blue-300" />}
                {getBossImageUrl(b.boss_image || b.boss_name) && <img src={getBossImageUrl(b.boss_image || b.boss_name)} alt="" className="w-5 h-5 rounded" />}
                <span className="max-w-[80px] truncate">{b.boss_name}</span>
                <Badge className="bg-blue-500/30 text-blue-200 text-[8px] px-1 py-0">
                  {isHunting ? "Hunting" : "Accepted"}
                </Badge>
              </Button>
            );
          })}
        </>
      )}
    </div>
  );
};
